import React from 'react';
import PropTypes from 'prop-types';

import {
  Accordion, AccordionDetails, AccordionSummary, Box, Typography, Paper, Button, Divider, Link, Stack, Dialog, DialogTitle, DialogContent, DialogActions,
} from 'views/components';
import {PersonOutline, CallOutlined, StorageOutlined, RuleOutlined} from 'views/icons';
import {translate, Translate} from 'views/module';

const ExternalApplicationCard = (props) => {
  const {externalApplication, jobBackend} = props;

  const statusPill = (status) => {
    switch (status) {
      case 'ACCEPTED':
        return {
          color: '#80FFB3',
          label: translate('career.jobDetail.Accepted'),
        };
      case 'REJECTED':
        return {
          color: '#767676',
          label: translate('career.jobDetail.Rejected'),
        };
      case 'PENDING':
      default:
        return {
          color: '#FFD60A',
          label: translate('career.jobDetail.Pending'),
        };
    }
  };
  const evaluationChip = (status) => {
    if (status === 'success') {
      return {
        bg: '#ecfdf5',
        border: '#6ee7b7',
        text: '#059669',
        label: translate('career.jobDetail.Evaluation.Success'),
      };
    }
    if (status === 'failure') {
      return {
        bg: '#fef2f2',
        border: '#fca5a5',
        text: '#ef4444',
        label: translate('career.jobDetail.Evaluation.Failure'),
      };
    }

    return {
      bg: '#f3f4f6',
      border: '#e5e7eb',
      text: '#6b7280',
      label: translate('career.jobDetail.Evaluation.Unknown'),
    };
  };

  const statusInfo = React.useMemo(() => statusPill(externalApplication.status), [externalApplication.status]);

  const firstName = externalApplication.contactInfo.firstName;
  const lastName = externalApplication.contactInfo.lastName;

  const evalObj = externalApplication.evaluation;
  const basic = evaluationChip(evalObj.basicQualification.status);
  const comms = evaluationChip(evalObj.communication.status);
  const motive = evaluationChip(evalObj.motivation.status);

  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const handleAccept = (e) => {
    e.stopPropagation();
    setConfirmOpen(true);
  };

  const handleDeny = (e) => {
    e.stopPropagation();
    jobBackend.externalApplication.denyApplication(externalApplication);
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    jobBackend.externalApplication.acceptApplication(externalApplication);
  };

  const handleCancel = () => {
    setConfirmOpen(false);
  };

  return (
    <>
      <Accordion>
        <AccordionSummary
          sx={{
            'alignItems': 'flex-end',
            'minHeight': 'unset',
            'pb': 2,
            'mt': 1,
            '& .MuiAccordionSummary-content': {m: 0},
            '& .MuiAccordionSummary-expandIconWrapper': {
              alignSelf: 'flex-end',
              mb: 9,
              mr: 2,
            },
          }}
        >
          <Box sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    backgroundColor: statusInfo.color,
                    borderRadius: 12,
                    px: 1.25,
                    py: 0.25,
                    fontWeight: 500,
                    fontSize: 14,
                    height: 24,
                  }}
                >
                  {statusInfo.label}
                </Box>
                <Typography variant="h6" sx={{fontWeight: 600}}>
                  {firstName} {lastName}
                </Typography>
              </Stack>

              {/* Action Buttons */}
              <Box sx={{display: 'flex', flexDirection: 'row', gap: 1.5, mt: 1, flex: 'none'}}>
                <Button
                  onClick={handleDeny}
                  mode="contained"
                  sx={{
                    borderRadius: 24,
                    px: 2.25,
                    py: 1,
                    minWidth: 100,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Translate expression="career.jobDetail.DenyApplication" />
                </Button>
                <Button
                  onClick={handleAccept}
                  mode="contained"
                  sx={{
                    borderRadius: 24,
                    px: 2.25,
                    py: 1,
                    minWidth: 100,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Translate expression="career.jobDetail.AcceptApplication" />
                </Button>
              </Box>
            </Box>

            <Box sx={{display: 'flex', flexWrap: 'wrap'}}>
              <Typography sx={{color: '#6b7280', mr: 2}}>{externalApplication.contactInfo.email}</Typography>
              <Typography sx={{color: '#6b7280'}}>{externalApplication.contactInfo.phoneNumber}</Typography>
            </Box>

            <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', mt: 1}}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: basic.bg,
                  color: basic.text,
                  border: `1px solid ${basic.border}`,
                  borderRadius: 12,
                  px: 1.5,
                  py: 0.5,
                  mr: 1,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                <Translate expression="career.jobDetail.ExternalApplications.Basic" substitute={{label: basic.label}} />
              </Box>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: comms.bg,
                  color: comms.text,
                  border: `1px solid ${comms.border}`,
                  borderRadius: 12,
                  px: 1.5,
                  py: 0.5,
                  mr: 1,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                <Translate expression="career.jobDetail.ExternalApplications.Communication" substitute={{label: comms.label}} />
              </Box>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: motive.bg,
                  color: motive.text,
                  border: `1px solid ${motive.border}`,
                  borderRadius: 12,
                  px: 1.5,
                  py: 0.5,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                <Translate expression="career.jobDetail.ExternalApplications.Motivation" substitute={{label: motive.label}} />
              </Box>
            </Box>
          </Box>
        </AccordionSummary>
        <Divider />
        <AccordionDetails>
          <Box
            sx={{display: 'grid', gap: 2, ml: 4, gridTemplateColumns: {xs: '1fr', md: '1fr 280px'}}}
          >
            <Box sx={{display: 'grid', gap: 2}}>

              {/* Biography Section */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#e5e7eb',
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                  <PersonOutline sx={{color: '#6b7280', mr: 1}} />
                  <Typography variant="h6" sx={{fontWeight: 600}}>
                    <Translate expression="career.jobDetail.ExternalApplications.Biography" />
                  </Typography>
                </Box>

                {/* Short Bio */}
                <Box sx={{mb: 1.5}}>
                  <Typography
                    variant="overline"
                    sx={{display: 'block', color: '#6b7280', fontWeight: 700, letterSpacing: 0.6, mb: 0.5}}
                  >
                    <Translate expression="career.jobDetail.ExternalApplications.ShortBio" />
                  </Typography>
                  <Typography variant="body2" sx={{color: '#6b7280e6'}}>
                    {externalApplication.biography.shortBio}
                  </Typography>
                </Box>

                {/* Personal Bio */}
                <Box sx={{mb: 1.5}}>
                  <Typography
                    variant="overline"
                    sx={{display: 'block', color: '#6b7280', fontWeight: 700, letterSpacing: 0.6, mb: 0.5}}
                  >
                    <Translate expression="career.jobDetail.ExternalApplications.PersonalBio" />
                  </Typography>
                  <Typography variant="body2" sx={{color: '#6b7280e6'}}>
                    {externalApplication.biography.personalBio}
                  </Typography>
                </Box>

                {/* Caregiver Motivation */}
                <Box>
                  <Typography
                    variant="overline"
                    sx={{display: 'block', color: '#6b7280', fontWeight: 700, letterSpacing: 0.6, mb: 0.5}}
                  >
                    <Translate expression="career.jobDetail.ExternalApplications.CaregiverMotivation" />
                  </Typography>
                  <Typography variant="body2" sx={{color: '#6b7280e6'}}>
                    {externalApplication.biography.caregiverMotivation}
                  </Typography>
                </Box>
              </Paper>

              {/* Data Collection Section */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#e5e7eb',
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                  <StorageOutlined sx={{color: '#6b7280', mr: 1}} />
                  <Typography variant="h6" sx={{fontWeight: 600}}>
                    <Translate expression="career.jobDetail.ExternalApplications.DataCollection" />
                  </Typography>
                </Box>
                {
                  Object.entries(externalApplication.dataCollection).map(([key, obj]) => {
                    if (obj.value === null) return null;
                    return (
                      <Box
                        key={key}
                        sx={{
                          backgroundColor: '#f9fafb',
                          border: '1px solid #e5e7eb',
                          borderRadius: 1,
                          p: 1.5,
                          mb: 1.5,
                        }}
                      >
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5}}>
                          <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                            {key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())}
                          </Typography>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              backgroundColor: '#f3f4f6',
                              borderRadius: 1.5,
                              px: 2.5,
                              py: 0.5,
                              fontWeight: 600,
                              fontSize: 13,
                              color: '#6b7280e6',
                            }}
                          >
                            {String(obj.value)}
                          </Box>
                        </Box>
                        <Box sx={{display: 'grid', alignItems: 'baseline'}}>
                          <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                            <Translate expression="career.jobDetail.ExternalApplications.Rationale" />
                          </Typography>
                          <Typography variant="body2" sx={{color: '#6b7280e6'}}>{obj.rationale}</Typography>
                        </Box>
                      </Box>
                    );
                  })
                }
              </Paper>

              {/* Evaluation Section */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#e5e7eb',
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                  <Box sx={{display: 'flex', alignItems: 'center'}}>
                    <RuleOutlined sx={{color: '#6b7280', mr: 1}} />
                    <Typography variant="h6" sx={{fontWeight: 600}}>
                      <Translate expression="career.jobDetail.ExternalApplications.EvaluationTitle" />
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      backgroundColor: '#f3f4f6',
                      color: '#6b7280',
                      border: '1px solid #e5e7eb',
                      borderRadius: 1.5,
                      px: 1.5,
                      py: 0.5,
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    {evalObj.screeningStatus}
                  </Box>
                </Box>

                {/* Basic Qualification */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: evalObj.basicQualification.status === 'success' ? '#ecfdf5' : '#fef2f2',
                    borderColor: evalObj.basicQualification.status === 'success' ? '#6ee7b7' : '#fca5a5',
                    mb: 1.5,
                  }}
                >
                  <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                    <Translate expression="career.jobDetail.ExternalApplications.BasicEval" />
                  </Typography>
                  <Box>
                    <Typography variant="body2" sx={{color: '#6b7280e6'}}>{evalObj.basicQualification.description}</Typography>
                  </Box>
                </Paper>

                {/* Communication */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: evalObj.communication.status === 'success' ? '#ecfdf5' : '#fef2f2',
                    borderColor: evalObj.communication.status === 'success' ? '#6ee7b7' : '#fca5a5',
                    mb: 1.5,
                  }}
                >
                  <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                    <Translate expression="career.jobDetail.ExternalApplications.CommunicationEval" />
                  </Typography>
                  <Box>
                    <Typography variant="body2" sx={{color: '#6b7280e6'}}>{evalObj.communication.description}</Typography>
                  </Box>
                </Paper>

                {/* Motivation */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    backgroundColor: evalObj.motivation.status === 'success' ? '#ecfdf5' : '#fef2f2',
                    borderColor: evalObj.motivation.status === 'success' ? '#6ee7b7' : '#fca5a5',
                  }}
                >
                  <Typography variant="subtitle1" sx={{fontWeight: 700}}>
                    <Translate expression="career.jobDetail.ExternalApplications.MotivationEval" />
                  </Typography>
                  <Box>
                    <Typography variant="body2" sx={{color: '#6b7280e6'}}>{evalObj.motivation.description}</Typography>
                  </Box>
                </Paper>
              </Paper>
            </Box>
            {/* Right Column: Contact Info only */}
            <Box sx={{display: 'grid', gap: 2, alignSelf: 'start', height: 'fit-content'}}>
              {/* Contact Info Section */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: '#fff',
                  borderColor: '#e5e7eb',
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                  <CallOutlined sx={{color: '#6b7280', mr: 1}} />
                  <Typography variant="h6" sx={{fontWeight: 600}}>
                    <Translate expression="career.jobDetail.ExternalApplications.ContactInfo" />
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                    <Translate expression="career.jobDetail.ExternalApplications.Email" />
                  </Typography>
                  <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                    {externalApplication.contactInfo.email}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                    <Translate expression="career.jobDetail.ExternalApplications.Phone" />
                  </Typography>
                  <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                    {externalApplication.contactInfo.phoneNumber}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                    <Translate expression="career.jobDetail.ExternalApplications.Street" />
                  </Typography>
                  <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                    {externalApplication.contactInfo.streetAddress}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                    <Translate expression="career.jobDetail.ExternalApplications.City" />
                  </Typography>
                  <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                    {externalApplication.contactInfo.city}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                    <Translate expression="career.jobDetail.ExternalApplications.Province" />
                  </Typography>
                  <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                    {externalApplication.contactInfo.province}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                    <Translate expression="career.jobDetail.ExternalApplications.PostalCode" />
                  </Typography>
                  <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                    {externalApplication.contactInfo.postalCode}
                  </Typography>
                </Box>
                {
                  externalApplication.convUrl &&
                  <Box>
                    <Typography variant="overline" sx={{color: '#6b7280', fontWeight: 700}}>
                      <Translate expression="career.jobDetail.ExternalApplications.ConversationUrl" />
                    </Typography>
                    <Typography variant="body2" sx={{color: '#3a3d44ff'}}>
                      <Link
                        href={externalApplication.convUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{color: '#1976d2', textDecoration: 'underline'}}
                      >
                        {externalApplication.convUrl}
                      </Link>
                    </Typography>
                  </Box>
                }
              </Paper>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
      {/* Dialog render */}
      {
        confirmOpen &&
        <Dialog open={confirmOpen} onClose={handleCancel}>
          <DialogTitle>
            <Translate expression="career.jobDetail.ConfirmAcceptTitle" />
          </DialogTitle>
          <DialogContent>
            <Typography>
              <Translate
                expression="career.jobDetail.ConfirmAcceptText"
                substitute={{name: `${externalApplication.contactInfo.firstName} ${externalApplication.contactInfo.lastName}`}}
              />
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancel} mode="text" sx={{borderRadius: 24, px: 2.25, py: 1, minWidth: 100, alignItems: 'center', justifyContent: 'center'}}>
              <Translate expression="generic.No" />
            </Button>
            <Button
              onClick={handleConfirm}
              mode="contained" color="primary"
              sx={{borderRadius: 24, px: 2.25, py: 1, minWidth: 100, alignItems: 'center', justifyContent: 'center'}}
            >
              <Translate expression="generic.Yes" />
            </Button>
          </DialogActions>
        </Dialog>
      }
    </>
  );
};

ExternalApplicationCard.propTypes = {
  externalApplication: PropTypes.object.isRequired,
  jobBackend: PropTypes.object.isRequired,
};

export default ExternalApplicationCard;
